import React, { useEffect, useState, useRef } from 'react';
import UserLayout from '../components/UserLayout';
import Button from '../components/Button';
import { UserIcon, MailIcon, PhoneIcon, MapPinIcon, CalendarIcon, CreditCardIcon, ClockIcon, UploadIcon, CheckIcon, XIcon, EyeIcon, EyeOffIcon, SaveIcon, EditIcon, LockIcon, ShieldIcon, BellIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, CameraIcon, MoonIcon, GlobeIcon, ArrowRightIcon, HelpCircleIcon, RefreshCwIcon, AlertTriangleIcon, LogOutIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
const UserProfilePage: React.FC = () => {
  const {
    user,
    updateUserProfile
  } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'preferences'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(user?.profileImage || null);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Form data states
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    dateOfBirth: user?.dateOfBirth || '',
    nin: user?.nin || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  // Reset success message after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    // Reset form data when canceling edit
    if (isEditing) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        address: user?.address || '',
        dateOfBirth: user?.dateOfBirth || '',
        nin: user?.nin || ''
      });
      setProfileImage(user?.profileImage || null);
      setUploadedImage(null);
    }
  };
  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      // Create updated user data with form data
      const updatedUserData = {
        ...formData
      };
      // If there's an uploaded image, add it to the updated data
      if (uploadedImage && profileImage) {
        updatedUserData.profileImage = profileImage;
      }
      // Call the update profile function from context
      if (updateUserProfile) {
        const success = await updateUserProfile(updatedUserData);
        if (success) {
          setSaveSuccess(true);
          setIsEditing(false);
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  const handleFile = (file: File) => {
    // Check if file is an image
    if (!file.type.match('image.*')) {
      alert('Please select an image file');
      return;
    }
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return;
    }
    // Create a URL for the file
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result) {
        setProfileImage(e.target.result as string);
        setUploadedImage(file);
      }
    };
    reader.readAsDataURL(file);
  };
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const removeImage = () => {
    setProfileImage(null);
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const savePasswordChanges = async () => {
    // Validate passwords
    setErrorMessage(null);
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMessage('New password and confirmation do not match');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }
    setIsSaving(true);
    try {
      // Call the update password function
      // This would be implemented in your AuthContext
      // For demo purposes, we'll just simulate a successful update
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Reset password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordModal(false);
      setSaveSuccess(true);
    } catch (error) {
      console.error('Error updating password:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const togglePasswordVisibility = (field: 'currentPassword' | 'newPassword' | 'confirmPassword') => {
    setPasswordVisibility({
      ...passwordVisibility,
      [field]: !passwordVisibility[field]
    });
  };
  const toggleTwoFactor = (enable: boolean) => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      setShowTwoFactorModal(false);
      setSaveSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }, 1500);
  };
  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
        setUploadedImage(file);
      };
      reader.readAsDataURL(file);
    }
  };
  const uploadProfileImage = () => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      if (previewUrl) {
        setProfileImage(previewUrl);
        setIsSaving(false);
        setShowImageUploadModal(false);
        setUploadedImage(null);
        setPreviewUrl(null);
        setSaveSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveSuccess(false);
        }, 3000);
      }
    }, 1500);
  };
  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <UserLayout data-id="element-6747">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-6748">
        <div className="max-w-7xl mx-auto" data-id="element-6749">
          {/* Page Header */}
          <div className="mb-6" data-id="element-6750">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6751">My Profile</h1>
            <p className="text-gray-600 mt-1" data-id="element-6752">
              Manage your personal information and account settings
            </p>
          </div>
          {/* Success Message */}
          {saveSuccess && <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md" data-id="element-6753">
              <div className="flex items-center" data-id="element-6754">
                <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-6755" />
                <p className="text-sm text-green-700" data-id="element-6756">
                  Your profile has been updated successfully.
                </p>
              </div>
            </div>}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-6757">
            {/* Profile Sidebar */}
            <div className="lg:col-span-1 space-y-6" data-id="element-6758">
              {/* Profile Card */}
              <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-6759">
                <div className="bg-green-600 h-24 relative" data-id="element-6760">
                  <div className="absolute -bottom-12 left-6" data-id="element-6761">
                    <div className="relative" data-id="element-6762">
                      {profileImage ? <img src={profileImage} alt={formData.name} className="w-24 h-24 rounded-full border-4 border-white object-cover" data-id="element-6763" /> : <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-4xl font-bold border-4 border-white" data-id="element-6764">
                          {formData.name.charAt(0) || 'U'}
                        </div>}
                      <button onClick={() => setShowImageUploadModal(true)} className="absolute bottom-0 right-0 bg-gray-100 rounded-full p-1 shadow-md hover:bg-gray-200" title="Change profile picture" data-id="element-6765">
                        <CameraIcon size={16} className="text-gray-600" data-id="element-6766" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pt-14 pb-6 px-6" data-id="element-6767">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-6768">
                    {formData.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-6769">User</p>
                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6770">
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-6771">
                      <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-6772" />
                      {formData.email}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-6773">
                      <PhoneIcon size={16} className="mr-3 text-gray-400" data-id="element-6774" />
                      {formData.phone || 'Not provided'}
                    </div>
                    {formData.address && <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-6775">
                        <MapPinIcon size={16} className="mr-3 text-gray-400" data-id="element-6776" />
                        {formData.address}
                      </div>}
                    {formData.dateOfBirth && <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-6777">
                        <CalendarIcon size={16} className="mr-3 text-gray-400" data-id="element-6778" />
                        <div data-id="element-6779">
                          <span className="block text-xs text-gray-500" data-id="element-6780">
                            Date of Birth
                          </span>
                          {formatDate(formData.dateOfBirth)}
                        </div>
                      </div>}
                  </div>
                  {/* Quick Actions */}
                  <div className="mt-6 space-y-2" data-id="element-6781">
                    <button onClick={() => setShowPasswordModal(true)} className="w-full flex items-center justify-between text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-6782">
                      <span className="flex items-center" data-id="element-6783">
                        <LockIcon size={16} className="mr-2" data-id="element-6784" />
                        Change Password
                      </span>
                      <ArrowRightIcon size={16} data-id="element-6785" />
                    </button>
                    <button onClick={() => setShowTwoFactorModal(true)} className="w-full flex items-center justify-between text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-6786">
                      <span className="flex items-center" data-id="element-6787">
                        <ShieldIcon size={16} className="mr-2" data-id="element-6788" />
                        Two-Factor Authentication
                      </span>
                      <ArrowRightIcon size={16} data-id="element-6789" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Security Status */}
              <div className="bg-white rounded-lg shadow p-6" data-id="element-6790">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-6791">
                  Security Status
                </h3>
                <div className="space-y-4" data-id="element-6792">
                  <div className="flex items-center justify-between" data-id="element-6793">
                    <div className="flex items-center" data-id="element-6794">
                      <LockIcon size={16} className="mr-3 text-gray-400" data-id="element-6795" />
                      <div data-id="element-6796">
                        <span className="block text-sm text-gray-900" data-id="element-6797">
                          Password
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-6798">
                          Last changed {formatDate('2023-06-10')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-6799">
                      <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" data-id="element-6800">
                        <CheckCircleIcon size={12} className="mr-1" data-id="element-6801" />
                        Good
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6802">
                    <div className="flex items-center" data-id="element-6803">
                      <ShieldIcon size={16} className="mr-3 text-gray-400" data-id="element-6804" />
                      <div data-id="element-6805">
                        <span className="block text-sm text-gray-900" data-id="element-6806">
                          Two-Factor Authentication
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-6807">
                          Not enabled
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-6808">
                      <span className="flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full" data-id="element-6809">
                        <XCircleIcon size={12} className="mr-1" data-id="element-6810" />
                        Disabled
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6811">
                    <div className="flex items-center" data-id="element-6812">
                      <BellIcon size={16} className="mr-3 text-gray-400" data-id="element-6813" />
                      <div data-id="element-6814">
                        <span className="block text-sm text-gray-900" data-id="element-6815">
                          Notifications
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-6816">
                          Enabled
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-6817">
                      <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" data-id="element-6818">
                        <CheckCircleIcon size={12} className="mr-1" data-id="element-6819" />
                        Enabled
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Help Box */}
              <div className="bg-white rounded-lg shadow p-4" data-id="element-6820">
                <div className="flex items-start" data-id="element-6821">
                  <div className="flex-shrink-0" data-id="element-6822">
                    <HelpCircleIcon size={20} className="text-blue-500" data-id="element-6823" />
                  </div>
                  <div className="ml-3" data-id="element-6824">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-6825">
                      Need help?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-6826">
                      If you need assistance with your account settings, please
                      contact our support team.
                    </p>
                    <a href="#" className="mt-2 text-sm text-blue-600 hover:text-blue-500 flex items-center" data-id="element-6827">
                      Contact Support
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Profile Content */}
            <div className="lg:col-span-2" data-id="element-6828">
              <div className="bg-white rounded-lg shadow" data-id="element-6829">
                {/* Tabs */}
                <div className="border-b border-gray-200" data-id="element-6830">
                  <nav className="flex -mb-px" data-id="element-6831">
                    <button onClick={() => setActiveTab('profile')} className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${activeTab === 'profile' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6832">
                      <UserIcon className="h-4 w-4 inline mr-2" data-id="element-6833" />
                      Personal Information
                    </button>
                    <button onClick={() => setActiveTab('security')} className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${activeTab === 'security' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6834">
                      <LockIcon className="h-4 w-4 inline mr-2" data-id="element-6835" />
                      Security
                    </button>
                    <button onClick={() => setActiveTab('preferences')} className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${activeTab === 'preferences' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6836">
                      <ClockIcon className="h-4 w-4 inline mr-2" data-id="element-6837" />
                      Preferences
                    </button>
                  </nav>
                </div>
                {/* Personal Information Tab */}
                {activeTab === 'profile' && <div className="p-6" data-id="element-6838">
                    <div className="flex justify-between items-center mb-6" data-id="element-6839">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-6840">
                        Personal Information
                      </h3>
                      <button onClick={handleEditToggle} className={`text-sm px-4 py-2 rounded-md ${isEditing ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-600'}`} data-id="element-6841">
                        {isEditing ? 'Cancel' : 'Edit Profile'}
                      </button>
                    </div>
                    <div className="space-y-6" data-id="element-6842">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6843">
                        <div data-id="element-6844">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6845">
                            Full Name
                          </label>
                          {isEditing ? <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-6846" /> : <p className="text-sm text-gray-900" data-id="element-6847">
                              {formData.name}
                            </p>}
                        </div>
                        <div data-id="element-6848">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6849">
                            Email Address
                          </label>
                          <p className="text-sm text-gray-900" data-id="element-6850">
                            {formData.email}
                          </p>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-6851">
                            Email cannot be changed
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6852">
                        <div data-id="element-6853">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6854">
                            Phone Number
                          </label>
                          {isEditing ? <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="e.g. 08012345678" data-id="element-6855" /> : <p className="text-sm text-gray-900" data-id="element-6856">
                              {formData.phone || 'Not provided'}
                            </p>}
                        </div>
                        <div data-id="element-6857">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6858">
                            Date of Birth
                          </label>
                          {isEditing ? <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-6859" /> : <p className="text-sm text-gray-900" data-id="element-6860">
                              {formData.dateOfBirth ? formatDate(formData.dateOfBirth) : 'Not provided'}
                            </p>}
                        </div>
                      </div>
                      <div data-id="element-6861">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6862">
                          Address
                        </label>
                        {isEditing ? <input type="text" name="address" value={formData.address} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Your residential address" data-id="element-6863" /> : <p className="text-sm text-gray-900" data-id="element-6864">
                            {formData.address || 'Not provided'}
                          </p>}
                      </div>
                      <div data-id="element-6865">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6866">
                          National Identification Number (NIN)
                        </label>
                        {isEditing ? <input type="text" name="nin" value={formData.nin} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Your NIN" data-id="element-6867" /> : <p className="text-sm text-gray-900" data-id="element-6868">
                            {formData.nin || 'Not provided'}
                          </p>}
                      </div>
                      {isEditing && <div className="flex justify-end" data-id="element-6869">
                          <button onClick={handleSaveProfile} disabled={isSaving} className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-6870">
                            {isSaving ? <>
                                <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-6871" />
                                Saving...
                              </> : <>
                                <SaveIcon size={16} className="mr-2" data-id="element-6872" />
                                Save Changes
                              </>}
                          </button>
                        </div>}
                    </div>
                  </div>}
                {/* Security Tab */}
                {activeTab === 'security' && <div className="p-6" data-id="element-6873">
                    <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-6874">
                      Security Settings
                    </h3>
                    <div className="space-y-6" data-id="element-6875">
                      {/* Password */}
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-6876">
                        <div className="flex justify-between items-start" data-id="element-6877">
                          <div className="flex items-start" data-id="element-6878">
                            <div className="mt-0.5" data-id="element-6879">
                              <LockIcon size={20} className="text-gray-500" data-id="element-6880" />
                            </div>
                            <div className="ml-3" data-id="element-6881">
                              <h4 className="text-sm font-medium text-gray-900" data-id="element-6882">
                                Password
                              </h4>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-6883">
                                Last changed on {formatDate('2023-06-10')}
                              </p>
                            </div>
                          </div>
                          <button onClick={() => setShowPasswordModal(true)} className="text-xs bg-white border border-gray-300 text-gray-700 px-2.5 py-1.5 rounded-md hover:bg-gray-50" data-id="element-6884">
                            Change
                          </button>
                        </div>
                      </div>
                      {/* Two-Factor Authentication */}
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-6885">
                        <div className="flex justify-between items-start" data-id="element-6886">
                          <div className="flex items-start" data-id="element-6887">
                            <div className="mt-0.5" data-id="element-6888">
                              <ShieldIcon size={20} className="text-gray-500" data-id="element-6889" />
                            </div>
                            <div className="ml-3" data-id="element-6890">
                              <h4 className="text-sm font-medium text-gray-900" data-id="element-6891">
                                Two-Factor Authentication
                              </h4>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-6892">
                                Disabled - Enable for additional security
                              </p>
                            </div>
                          </div>
                          <button onClick={() => setShowTwoFactorModal(true)} className="text-xs bg-green-50 border border-green-300 text-green-700 px-2.5 py-1.5 rounded-md hover:bg-green-100" data-id="element-6893">
                            Enable
                          </button>
                        </div>
                      </div>
                      {/* Account Activity */}
                      <div data-id="element-6894">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6895">
                          Recent Account Activity
                        </h4>
                        <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-id="element-6896">
                          <div className="p-4 border-b border-gray-200" data-id="element-6897">
                            <div className="flex items-start" data-id="element-6898">
                              <div className="flex-shrink-0 bg-blue-100 rounded-full p-1" data-id="element-6899">
                                <UserIcon size={16} className="text-blue-600" data-id="element-6900" />
                              </div>
                              <div className="ml-3" data-id="element-6901">
                                <p className="text-sm font-medium text-gray-900" data-id="element-6902">
                                  Successful login
                                </p>
                                <p className="text-xs text-gray-500 mt-1" data-id="element-6903">
                                  {new Date().toLocaleDateString()} at{' '}
                                  {new Date().toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4 border-b border-gray-200" data-id="element-6904">
                            <div className="flex items-start" data-id="element-6905">
                              <div className="flex-shrink-0 bg-green-100 rounded-full p-1" data-id="element-6906">
                                <LockIcon size={16} className="text-green-600" data-id="element-6907" />
                              </div>
                              <div className="ml-3" data-id="element-6908">
                                <p className="text-sm font-medium text-gray-900" data-id="element-6909">
                                  Password changed
                                </p>
                                <p className="text-xs text-gray-500 mt-1" data-id="element-6910">
                                  {formatDate('2023-06-10')} at 14:20
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4" data-id="element-6911">
                            <div className="flex items-start" data-id="element-6912">
                              <div className="flex-shrink-0 bg-purple-100 rounded-full p-1" data-id="element-6913">
                                <EditIcon size={16} className="text-purple-600" data-id="element-6914" />
                              </div>
                              <div className="ml-3" data-id="element-6915">
                                <p className="text-sm font-medium text-gray-900" data-id="element-6916">
                                  Profile updated
                                </p>
                                <p className="text-xs text-gray-500 mt-1" data-id="element-6917">
                                  {formatDate('2023-05-15')} at 10:30
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-right" data-id="element-6918">
                          <button className="text-xs text-blue-600 hover:text-blue-800" data-id="element-6919">
                            View all activity
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>}
                {/* Preferences Tab */}
                {activeTab === 'preferences' && <div className="p-6" data-id="element-6920">
                    <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-6921">
                      Notification Preferences
                    </h3>
                    <div className="bg-gray-50 p-6 rounded-md space-y-6" data-id="element-6922">
                      <div data-id="element-6923">
                        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-6924">
                          Email Notifications
                        </h4>
                        <div className="space-y-4" data-id="element-6925">
                          <div className="flex items-start" data-id="element-6926">
                            <div className="flex items-center h-5" data-id="element-6927">
                              <input id="application_updates" name="application_updates" type="checkbox" className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded" defaultChecked data-id="element-6928" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-6929">
                              <label htmlFor="application_updates" className="font-medium text-gray-700" data-id="element-6930">
                                Application Updates
                              </label>
                              <p className="text-gray-500" data-id="element-6931">
                                Receive email notifications about your
                                application status changes
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-6932">
                            <div className="flex items-center h-5" data-id="element-6933">
                              <input id="payment_confirmations" name="payment_confirmations" type="checkbox" className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded" defaultChecked data-id="element-6934" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-6935">
                              <label htmlFor="payment_confirmations" className="font-medium text-gray-700" data-id="element-6936">
                                Payment Confirmations
                              </label>
                              <p className="text-gray-500" data-id="element-6937">
                                Receive email notifications for payment
                                confirmations
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-6938">
                            <div className="flex items-center h-5" data-id="element-6939">
                              <input id="service_announcements" name="service_announcements" type="checkbox" className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded" defaultChecked data-id="element-6940" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-6941">
                              <label htmlFor="service_announcements" className="font-medium text-gray-700" data-id="element-6942">
                                Service Announcements
                              </label>
                              <p className="text-gray-500" data-id="element-6943">
                                Receive updates about new services and features
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-6944">
                            <div className="flex items-center h-5" data-id="element-6945">
                              <input id="marketing_emails" name="marketing_emails" type="checkbox" className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded" data-id="element-6946" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-6947">
                              <label htmlFor="marketing_emails" className="font-medium text-gray-700" data-id="element-6948">
                                Marketing Emails
                              </label>
                              <p className="text-gray-500" data-id="element-6949">
                                Receive promotional emails and newsletters
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div data-id="element-6950">
                        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-6951">
                          SMS Notifications
                        </h4>
                        <div className="space-y-4" data-id="element-6952">
                          <div className="flex items-start" data-id="element-6953">
                            <div className="flex items-center h-5" data-id="element-6954">
                              <input id="sms_application_updates" name="sms_application_updates" type="checkbox" className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded" defaultChecked data-id="element-6955" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-6956">
                              <label htmlFor="sms_application_updates" className="font-medium text-gray-700" data-id="element-6957">
                                Application Updates
                              </label>
                              <p className="text-gray-500" data-id="element-6958">
                                Receive SMS notifications about your application
                                status changes
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-6959">
                            <div className="flex items-center h-5" data-id="element-6960">
                              <input id="sms_payment_confirmations" name="sms_payment_confirmations" type="checkbox" className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded" defaultChecked data-id="element-6961" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-6962">
                              <label htmlFor="sms_payment_confirmations" className="font-medium text-gray-700" data-id="element-6963">
                                Payment Confirmations
                              </label>
                              <p className="text-gray-500" data-id="element-6964">
                                Receive SMS notifications for payment
                                confirmations
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="pt-4 flex justify-end" data-id="element-6965">
                        <button className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-6966">
                          <SaveIcon className="h-4 w-4 mr-2" data-id="element-6967" />
                          Save Preferences
                        </button>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Change Password Modal */}
      {showPasswordModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-6968">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-6969">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-6970">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-6971">
                Change Password
              </h3>
            </div>
            <div className="p-6" data-id="element-6972">
              <div className="space-y-4" data-id="element-6973">
                <div data-id="element-6974">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6975">
                    Current Password
                  </label>
                  <div className="relative" data-id="element-6976">
                    <input type={passwordVisibility.currentPassword ? 'text' : 'password'} name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-6977" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => togglePasswordVisibility('currentPassword')} data-id="element-6978">
                      {passwordVisibility.currentPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-6979" /> : <EyeIcon className="h-5 w-5" data-id="element-6980" />}
                    </button>
                  </div>
                </div>
                <div data-id="element-6981">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6982">
                    New Password
                  </label>
                  <div className="relative" data-id="element-6983">
                    <input type={passwordVisibility.newPassword ? 'text' : 'password'} name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-6984" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => togglePasswordVisibility('newPassword')} data-id="element-6985">
                      {passwordVisibility.newPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-6986" /> : <EyeIcon className="h-5 w-5" data-id="element-6987" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-6988">
                    Password must be at least 8 characters and include
                    uppercase, lowercase, number, and special character.
                  </p>
                </div>
                <div data-id="element-6989">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6990">
                    Confirm New Password
                  </label>
                  <div className="relative" data-id="element-6991">
                    <input type={passwordVisibility.confirmPassword ? 'text' : 'password'} name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-6992" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => togglePasswordVisibility('confirmPassword')} data-id="element-6993">
                      {passwordVisibility.confirmPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-6994" /> : <EyeIcon className="h-5 w-5" data-id="element-6995" />}
                    </button>
                  </div>
                </div>
                {errorMessage && <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md" data-id="element-6996">
                    <div className="flex items-center" data-id="element-6997">
                      <XCircleIcon size={16} className="text-red-500 mr-2" data-id="element-6998" />
                      <p className="text-sm text-red-700" data-id="element-6999">{errorMessage}</p>
                    </div>
                  </div>}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-7000">
              <button onClick={() => {
            setShowPasswordModal(false);
            setErrorMessage(null);
            setPasswordData({
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            });
          }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-7001">
                Cancel
              </button>
              <button onClick={savePasswordChanges} disabled={isSaving} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-7002">
                {isSaving ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>}
      {/* Two-Factor Authentication Modal */}
      {showTwoFactorModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-7003">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-7004">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-7005">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-7006">
                Enable Two-Factor Authentication
              </h3>
            </div>
            <div className="p-6" data-id="element-7007">
              <div className="space-y-4" data-id="element-7008">
                <div className="flex items-center bg-green-50 p-4 rounded-md" data-id="element-7009">
                  <ShieldIcon size={20} className="text-green-600 mr-3" data-id="element-7010" />
                  <div data-id="element-7011">
                    <h4 className="text-sm font-medium text-green-800" data-id="element-7012">
                      Enhanced Security
                    </h4>
                    <p className="text-sm text-green-700 mt-1" data-id="element-7013">
                      Two-factor authentication adds an extra layer of security
                      to your account.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500" data-id="element-7014">
                  When enabled, you'll need to provide a verification code from
                  your mobile device in addition to your password when signing
                  in.
                </p>
                <div className="mt-4" data-id="element-7015">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-7016">
                    Setup Process:
                  </h4>
                  <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2" data-id="element-7017">
                    <li data-id="element-7018">
                      Download an authenticator app like Google Authenticator or
                      Microsoft Authenticator
                    </li>
                    <li data-id="element-7019">
                      Scan the QR code that will be displayed after you click
                      "Enable"
                    </li>
                    <li data-id="element-7020">
                      Enter the verification code from the app to confirm setup
                    </li>
                  </ol>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-7021">
              <button onClick={() => setShowTwoFactorModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-7022">
                Cancel
              </button>
              <button onClick={() => toggleTwoFactor(true)} disabled={isSaving} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-7023">
                {isSaving ? 'Processing...' : 'Enable Two-Factor'}
              </button>
            </div>
          </div>
        </div>}
      {/* Profile Image Upload Modal */}
      {showImageUploadModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-7024">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-7025">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-7026">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-7027">
                Update Profile Picture
              </h3>
            </div>
            <div className="p-6" data-id="element-7028">
              <div className="flex flex-col items-center space-y-4" data-id="element-7029">
                <div className="w-32 h-32 relative rounded-full overflow-hidden border-2 border-gray-200" data-id="element-7030">
                  <img src={previewUrl || profileImage || 'https://via.placeholder.com/200'} alt="Profile" className="w-full h-full object-cover" data-id="element-7031" />
                </div>
                <label className="cursor-pointer bg-gray-50 text-gray-700 px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100 flex items-center" data-id="element-7032">
                  <UploadIcon size={16} className="mr-2" data-id="element-7033" />
                  <span data-id="element-7034">Choose Image</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleProfileImageChange} data-id="element-7035" />
                </label>
                <p className="text-xs text-gray-500" data-id="element-7036">
                  Recommended: Square image, at least 200x200 pixels. Max size:
                  2MB.
                </p>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-7037">
              <button onClick={() => {
            setShowImageUploadModal(false);
            setUploadedImage(null);
            setPreviewUrl(null);
          }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-7038">
                Cancel
              </button>
              <button onClick={uploadProfileImage} disabled={isSaving || !previewUrl} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 ${!previewUrl ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-7039">
                {isSaving ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
          </div>
        </div>}
    </UserLayout>;
};
export default UserProfilePage;